import {
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
} from "react"

import { InputError } from "@/components/input-error"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

interface FormFieldProps {
  label: string
  htmlFor: string
  error?: string
  children: ReactNode
  description?: string
  required?: boolean
  className?: string
}

export function FormField({
  label,
  htmlFor,
  error,
  children,
  description,
  required,
  className,
}: FormFieldProps) {
  const errorId = `${htmlFor}-error`
  const hasError = !!error

  // Clone the first valid React element child to inject aria attributes
  const enhancedChildren = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        "aria-invalid": hasError || undefined,
        "aria-describedby": hasError ? errorId : undefined,
      })
    : children

  return (
    <div data-slot="form-field" className={cn("grid gap-2", className)}>
      <Label htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="text-destructive ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      {enhancedChildren}
      {description && (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}
      <InputError
        id={hasError ? errorId : undefined}
        messages={error ? [error] : undefined}
      />
    </div>
  )
}
